import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Eye,
  KeyRound,
  PackageCheck,
  RefreshCcw,
  Search,
  X,
  ShoppingBag,
  IndianRupee,
  Truck,
  CreditCard,
  MapPin,
  Navigation,
  Loader2,
} from "lucide-react";
import { warehouseInventoryService } from "../../../services/warehouseInventory.service";
import { orderPendingPaymentService } from "../../../services/orderPendingPayment.service";
import { env } from "../../../config/env";
import { ReportsService } from "../../../services/reports.service";
import {
  GoogleMap,
  MarkerF,
  DirectionsRenderer,
  useJsApiLoader,
} from "@react-google-maps/api";

const BORDER = "#f2c6cc";
const PAGE_SIZE = 10;

const TRACKING_REFRESH_MS = 20000;
const ORDER_LIST_REFRESH_MS = 10000;
const UNCLEAR_PENDING_CLOSE_MINUTES = 15;

const LIVE_TRACKING_STATUSES = new Set([
  "ASSIGNED",
  "PICKUP_PENDING",
  "PICKED_UP",
  "OUT_FOR_DELIVERY",
]);

const ORDER_STATUS_FILTERS = [
  { value: "", label: "All order statuses" },
  { value: "PROCESSING", label: "Placed / Processing" },
  { value: "PACKED", label: "Packed" },
  { value: "DELIVERY_ASSIGNED", label: "Driver Assigned" },
  { value: "READY_FOR_DISPATCH", label: "Ready For Dispatch" },
  { value: "PICKED_UP", label: "Picked Up" },
  { value: "OUT_FOR_DELIVERY", label: "Out For Delivery" },
  { value: "DISPATCHED", label: "Dispatched" },
  { value: "DELIVERED", label: "Completed / Delivered" },
  { value: "CANCELLED", label: "Cancelled" },
];

const PAYMENT_STATUS_FILTERS = [
  { value: "", label: "All payment statuses" },
  { value: "PAID", label: "Payment Paid" },
  { value: "PENDING", label: "Payment Pending (Online + POD)" },
  { value: "FAILED", label: "Payment Failed" },
  { value: "REFUNDED,PARTIALLY_REFUNDED", label: "Refunded / Partially Refunded" },
];

const PAYMENT_METHOD_FILTERS = [
  { value: "", label: "All payment types" },
  { value: "ONLINE", label: "Online" },
  { value: "PAY_ON_DELIVERY", label: "Pay on Delivery" },
];

function fmtDate(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function toLocalDateInput(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getLastThirtyDaysRange() {
  const today = new Date();
  const start = new Date(today);
  start.setDate(today.getDate() - 29);

  return {
    fromDate: toLocalDateInput(start),
    toDate: toLocalDateInput(today),
  };
}

function fmtDateTime(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fmtAnyDateTime(value) {
  if (!value) return "-";
  const raw = typeof value === "number" && value < 1000000000000 ? value * 1000 : value;
  return fmtDateTime(raw);
}

function fmtMoney(value) {
  return `₹${Number(value || 0).toFixed(2)}`;
}

function fmtQty(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return "0";
  return Number.isInteger(n) ? String(n) : n.toFixed(3).replace(/\.0+$/, "").replace(/(\.\d*?)0+$/, "$1");
}

function normalizeBatchAllocations(item = {}) {
  const rows =
    item?.batchAllocations ||
    item?.batch_allocations ||
    item?.product?.batchAllocations ||
    item?.product?.batch_allocations ||
    [];

  return Array.isArray(rows) ? rows : [];
}

function BatchStack({ rows, render, empty = "-" }) {
  if (!rows.length) return <span className="text-slate-400">{empty}</span>;

  return (
    <div className="space-y-1.5">
      {rows.map((batch, index) => (
        <div key={`${batch.batchId || batch.batchNo || index}`}>
          {render(batch, index)}
        </div>
      ))}
    </div>
  );
}


function getErrorMessage(err) {
  return err?.response?.data?.message || err?.message || "Something went wrong";
}

function normalizeStatus(status) {
  return String(status || "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function statusClass(status) {
  const v = String(status || "").toLowerCase();

  if (["delivered", "completed"].includes(v)) {
    return "bg-emerald-50 text-emerald-700 border-emerald-200";
  }

  if (["ready_for_dispatch", "pickup_pending"].includes(v)) {
    return "bg-sky-50 text-sky-700 border-sky-200";
  }

  if (["delivery_assigned", "assigned"].includes(v)) {
    return "bg-indigo-50 text-indigo-700 border-indigo-200";
  }

  if (["picked_up", "out_for_delivery", "dispatched"].includes(v)) {
    return "bg-violet-50 text-violet-700 border-violet-200";
  }

  if (["placed", "processing", "pending", "payment_pending"].includes(v)) {
    return "bg-amber-50 text-amber-700 border-amber-200";
  }

  if (["cancelled", "rejected", "failed", "payment_failed"].includes(v)) {
    return "bg-rose-50 text-rose-700 border-rose-200";
  }

  return "bg-slate-50 text-slate-700 border-slate-200";
}

function paymentClass(status) {
  const v = String(status || "").toLowerCase();

  if (["paid", "success", "completed"].includes(v)) {
    return "bg-emerald-50 text-emerald-700 border-emerald-200";
  }
  if (["pending", "authorized", "payment_pending_on_delivery", "refund_pending"].includes(v)) {
    return "bg-amber-50 text-amber-700 border-amber-200";
  }
  if (["failed", "refunded", "refund_failed", "partially_refunded"].includes(v)) {
    return "bg-rose-50 text-rose-700 border-rose-200";
  }

  return "bg-slate-50 text-slate-700 border-slate-200";
}


function getOrderStatusValue(order = {}) {
  return String(order?.status || order?.orderStatus || order?.order_status || "")
    .trim()
    .toUpperCase();
}

function getPaymentStatusValue(order = {}) {
  return String(order?.paymentStatus || order?.payment_status || "")
    .trim()
    .toUpperCase();
}

function isPayOnDeliveryOrder(order = {}) {
  return String(order?.paymentMethod || order?.payment_method || '').trim().toUpperCase() === 'PAY_ON_DELIVERY';
}

function isPaymentFailedOrder(order = {}) {
  const status = getOrderStatusValue(order);
  const paymentStatus = getPaymentStatusValue(order);
  const gatewayStatus = String(order?.gatewayPaymentStatus || order?.gateway_payment_status || "")
    .trim()
    .toUpperCase();

  return status === "PAYMENT_FAILED" || paymentStatus === "FAILED" || gatewayStatus === "FAILED";
}

function isWarehouseOrderTerminal(order = {}) {
  const status = getOrderStatusValue(order);
  const paymentStatus = getPaymentStatusValue(order);

  return (
    isPaymentFailedOrder(order) ||
    ["CANCELLED", "DELIVERED", "REJECTED"].includes(status) ||
    ["REFUNDED"].includes(paymentStatus)
  );
}

function getGatewayStatusValue(order = {}) {
  return String(order?.gatewayPaymentStatus || order?.gateway_payment_status || "")
    .trim()
    .toUpperCase();
}

function getPaymentDisplayLabel(order = {}) {
  const paymentStatus = getPaymentStatusValue(order);
  const gatewayStatus = getGatewayStatusValue(order);
  if (isPayOnDeliveryOrder(order)) {
    if (paymentStatus === 'PAID') return 'Pay on Delivery Paid';
    if (gatewayStatus === 'QR_CLOSED' || gatewayStatus === 'QR_CLOSED_CLOSED') return 'QR Closed';
    if (paymentStatus === 'PAYMENT_PENDING_ON_DELIVERY') return 'Pay on Delivery Pending';
    return `Pay on Delivery ${normalizeStatus(paymentStatus || '-')}`;
  }
  if (paymentStatus === 'PENDING' && gatewayStatus === 'AUTHORIZED') return 'AUTHORIZED';
  if (paymentStatus === 'PENDING' && gatewayStatus.startsWith('REFUND_')) return normalizeStatus(gatewayStatus);
  return order?.paymentStatus || order?.payment_status || '-';
}

function orderAgeMinutes(order = {}) {
  const created = new Date(order?.createdAt || order?.created_at || 0).getTime();
  if (!Number.isFinite(created) || created <= 0) return Number.POSITIVE_INFINITY;
  return Math.max(0, (Date.now() - created) / 60000);
}

function isUnclearPendingOrder(order = {}) {
  if (String(order?.paymentMethod || order?.payment_method || '').toUpperCase() !== 'ONLINE') return false;
  if (getOrderStatusValue(order) !== 'PAYMENT_PENDING' || getPaymentStatusValue(order) !== 'PENDING') return false;
  const gateway = getGatewayStatusValue(order);
  if (gateway.includes('AUTHORIZED') || gateway.includes('CAPTURED') || gateway.includes('REFUND')) return false;
  return orderAgeMinutes(order) >= UNCLEAR_PENDING_CLOSE_MINUTES;
}

function getPaymentMethodLabel(order = {}) {
  if (isPayOnDeliveryOrder(order)) return 'Razorpay Pay on Delivery';
  const method = String(order?.paymentMethod || order?.payment_method || '').trim().toUpperCase();
  if (method === 'ONLINE') return 'Online Payment';
  return order?.paymentMethod || order?.payment_method || '-';
}

function getPayOnDeliveryInfo(order = {}) {
  return order?.payOnDelivery || order?.pay_on_delivery || order?.pricing?.payOnDelivery || order?.pricing?.pay_on_delivery || null;
}

function isWarehouseOrderPaymentUnlocked(order = {}) {
  const paymentStatus = getPaymentStatusValue(order);
  return paymentStatus === "PAID" || (isPayOnDeliveryOrder(order) && paymentStatus === "PAYMENT_PENDING_ON_DELIVERY");
}

function getReadableCustomerOrderId(order) {
  const rawId =
    order?.orderNumber ||
    order?.order_number ||
    order?.order_code ||
    order?.orderCode ||
    order?.id ||
    "";

  if (!rawId) return "-";

  const text = String(rawId).trim();

  if (text.startsWith("ORD-")) return text;

  return `ORD-${text.replace(/-/g, "").slice(0, 8).toUpperCase()}`;
}

function getWarehouseDailyNumber(order = {}) {
  const raw = order?.warehouseDailyNumber ?? order?.warehouse_daily_number ?? null;
  const value = Number(raw);
  return Number.isInteger(value) && value > 0 ? value : null;
}

function WarehouseDailyNumberBadge({ order, compact = false }) {
  const value = getWarehouseDailyNumber(order);
  if (!value) return null;

  return (
    <span
      className={[
        "inline-flex items-center justify-center rounded-[12px] border border-[#f0b8c0] bg-gradient-to-br from-[#fff1f3] to-[#ffe5e9] font-black leading-none text-[#c94f61] shadow-[0_6px_16px_rgba(216,106,120,0.14)]",
        compact ? "min-w-[54px] px-2.5 py-2 text-[18px]" : "min-w-[76px] px-4 py-3 text-[28px]",
      ].join(" ")}
      title="Warehouse packet number"
    >
      #{value}
    </span>
  );
}

function getPricing(order) {
  const pricing = order?.pricing || {};

  const subtotal = Number(
    pricing?.subtotal ?? order?.subtotal ?? 0
  );

  const deliveryFee = Number(
    pricing?.deliveryFee ?? order?.deliveryFee ?? 0
  );

  const taxAmount = Number(
    pricing?.taxAmount ?? order?.taxAmount ?? 0
  );

  const discountAmount = Number(
    pricing?.discountAmount ?? order?.discountAmount ?? 0
  );

  const grandTotal = Number(
    pricing?.grandTotal ?? order?.grandTotal ?? subtotal + deliveryFee + taxAmount - discountAmount
  );

  return {
    subtotal,
    deliveryFee,
    taxAmount,
    discountAmount,
    grandTotal,
  };
}

function isCancelledRefundedOrder(order = {}) {
  const status = getOrderStatusValue(order);
  const paymentStatus = getPaymentStatusValue(order);
  return status === "CANCELLED" || paymentStatus === "REFUNDED";
}

function getCancellationInfo(order = {}) {
  const pricing = order?.pricing || {};
  const cancellation =
    order?.cancellation ||
    order?.cancel ||
    pricing?.cancellation ||
    pricing?.cancel ||
    {};
  const refund = order?.refund || cancellation?.refund || pricing?.refund || {};
  const rawRefund = refund?.raw || {};
  const stockRollback =
    order?.stockRollback ||
    order?.stock_rollback ||
    cancellation?.stockRollback ||
    cancellation?.stock_rollback ||
    {};
  const dispatchCancel =
    order?.dispatchCancel ||
    order?.dispatch_cancel ||
    cancellation?.dispatchCancel ||
    cancellation?.dispatch_cancel ||
    {};

  const refundAmountPaise = refund?.amount ?? rawRefund?.amount ?? null;
  const refundAmountRupees =
    refund?.amountRupees ??
    refund?.amount_rupees ??
    (refundAmountPaise != null ? Number(refundAmountPaise || 0) / 100 : null);

  return {
    hasCancellation:
      isCancelledRefundedOrder(order) ||
      Boolean(cancellation?.cancelled || refund?.id || refund?.refundId || refund?.refund_id),
    reason:
      cancellation?.reason ||
      cancellation?.cancelReason ||
      cancellation?.cancel_reason ||
      order?.cancelReason ||
      rawRefund?.notes?.reason ||
      "-",
    cancelledAt:
      cancellation?.cancelledAt ||
      cancellation?.cancelled_at ||
      order?.cancelledAt ||
      order?.cancelled_at ||
      null,
    source: cancellation?.source || "ADMIN",
    refundId: refund?.refundId || refund?.refund_id || refund?.id || rawRefund?.id || "-",
    refundStatus: refund?.status || rawRefund?.status || (String(order?.paymentStatus || "").toUpperCase() === "REFUNDED" ? "processed" : "-"),
    paymentId: refund?.paymentId || refund?.payment_id || rawRefund?.paymentId || rawRefund?.payment_id || "-",
    refundAmountRupees,
    speed: refund?.speedProcessed || refund?.speed_processed || rawRefund?.speed_processed || rawRefund?.speed_requested || "normal",
    refundCreatedAt: refund?.createdAt || refund?.created_at || rawRefund?.created_at || null,
    stockRollback,
    dispatchCancel,
  };
}

function DeliveryDriverCell({ order, delivery }) {
  const partner = delivery?.partner || null;
  const taskStatus = String(delivery?.taskStatus || "").toUpperCase();

  const taskLabel =
    taskStatus === "ASSIGNED"
      ? "Waiting for warehouse dispatch"
      : taskStatus === "PICKUP_PENDING"
        ? "Ready for pickup"
        : delivery?.taskStatus
          ? normalizeStatus(delivery.taskStatus)
          : "Pending";

  if (!partner) {
    if (isPaymentFailedOrder(order)) {
      return (
        <div>
          <div className="text-[13px] font-semibold text-rose-600">
            Payment failed
          </div>
          <div className="mt-1 text-[12px] text-slate-400">
            Driver flow closed
          </div>
        </div>
      );
    }

    if (isCancelledRefundedOrder(order)) {
      return (
        <div>
          <div className="text-[13px] font-semibold text-rose-600">
            Order closed
          </div>
          <div className="mt-1 text-[12px] text-slate-400">
            Driver flow closed
          </div>
        </div>
      );
    }

    return (
      <div>
        <div className="text-[13px] font-semibold text-amber-600">
          Not assigned
        </div>
        <div className="mt-1 text-[12px] text-slate-400">
          Waiting for driver assignment
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="font-semibold text-slate-800">{partner.name || "-"}</div>
      <div className="mt-1 text-[12px] text-slate-400">{partner.phone || "-"}</div>
      <div className="mt-1 text-[12px] text-slate-400">
        {[partner.vehicleType, partner.vehicleNumber].filter(Boolean).join(" / ") || "-"}
      </div>
      <div className="mt-1 text-[11px] font-semibold text-[#d86a78]">
        {taskLabel}
      </div>
    </div>
  );
}

function DeliveryDetailCard({ order, delivery }) {
  const partner = delivery?.partner || null;

  return (
    <div className="mt-5 rounded-[16px] border border-[#f2c6cc] p-4">
      <h4 className="text-[15px] font-bold text-slate-800">Delivery Partner Info</h4>

      {partner ? (
        <div className="mt-3 grid gap-3 text-[14px] text-slate-700 md:grid-cols-2 xl:grid-cols-3">
          <div><span className="font-semibold">Name:</span> {partner.name || "-"}</div>
          <div><span className="font-semibold">Phone:</span> {partner.phone || "-"}</div>
          <div><span className="font-semibold">Partner ID:</span> {partner.partnerId || "-"}</div>
          <div><span className="font-semibold">Email:</span> {partner.email || "-"}</div>
          <div><span className="font-semibold">Vehicle:</span> {[partner.vehicleType, partner.vehicleNumber].filter(Boolean).join(" / ") || "-"}</div>
          <div><span className="font-semibold">City/Hub:</span> {[partner.city, partner.hub].filter(Boolean).join(" / ") || "-"}</div>
          <div><span className="font-semibold">Assignment:</span> {delivery?.assignmentStatus ? normalizeStatus(delivery.assignmentStatus) : "-"}</div>
          <div><span className="font-semibold">Task:</span> {delivery?.taskStatus ? normalizeStatus(delivery.taskStatus) : "-"}</div>
          <div><span className="font-semibold">Received:</span> {fmtDateTime(delivery?.receivedAt)}</div>
          <div><span className="font-semibold">Picked Up:</span> {fmtDateTime(delivery?.pickedUpAt)}</div>
          <div><span className="font-semibold">Out For Delivery:</span> {fmtDateTime(delivery?.outForDeliveryAt)}</div>
          <div><span className="font-semibold">Delivered:</span> {fmtDateTime(delivery?.deliveredAt)}</div>
        </div>
      ) : (
        <div
          className={[
            "mt-3 rounded-[12px] px-3 py-2 text-[14px] font-semibold",
            isPaymentFailedOrder(order) || isCancelledRefundedOrder(order) || String(delivery?.taskStatus || "").toUpperCase() === "CANCELLED"
              ? "bg-rose-50 text-rose-700"
              : "bg-amber-50 text-amber-700",
          ].join(" ")}
        >
          {isPaymentFailedOrder(order)
            ? "Payment failed. Driver assignment is closed for this order."
            : isCancelledRefundedOrder(order)
              ? "Order closed. Driver flow is not active."
              : String(delivery?.taskStatus || "").toUpperCase() === "CANCELLED"
                ? `Delivery task cancelled. Cancelled at: ${fmtDateTime(delivery?.cancelledAt)}`
                : `Driver has not been assigned yet. Task status: ${delivery?.taskStatus ? normalizeStatus(delivery.taskStatus) : "Pending Assignment"}`}
        </div>
      )}
    </div>
  );
}

function CancellationRefundCard({ order }) {
  const info = getCancellationInfo(order);
  if (!info.hasCancellation) return null;

  const stockDone = Boolean(info.stockRollback?.done);
  const stockQty = info.stockRollback?.totalQty ?? info.stockRollback?.total_qty ?? 0;
  const batchCount = info.stockRollback?.updatedBatchCount ?? info.stockRollback?.updated_batch_count ?? 0;
  const dispatchCancelled = Boolean(info.dispatchCancel?.cancelled || info.dispatchCancel?.success);
  const refundStatus = String(info.refundStatus || "").toLowerCase();
  const refundSuccess = refundStatus === "processed" || refundStatus === "refunded" || refundStatus === "success";

  return (
    <div className="mt-5 rounded-[16px] border border-emerald-200 bg-emerald-50/60 p-4">
      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
        <div>
          <h4 className="text-[15px] font-black text-emerald-800">
            Refund & Cancellation Completed
          </h4>
          <p className="mt-1 text-[13px] font-semibold text-emerald-700">
            {refundSuccess ? "Razorpay refund processed successfully." : "Order has been cancelled. Check refund status below."}
          </p>
        </div>
        <span className="w-fit rounded-full border border-emerald-200 bg-white px-3 py-1 text-[11px] font-black uppercase tracking-[0.08em] text-emerald-700">
          {refundSuccess ? "Refund Successful" : normalizeStatus(info.refundStatus)}
        </span>
      </div>

      <div className="mt-4 grid gap-3 text-[13px] text-slate-700 md:grid-cols-2 xl:grid-cols-3">
        <div><span className="font-bold">Cancel Reason:</span> {info.reason}</div>
        <div><span className="font-bold">Cancelled At:</span> {fmtAnyDateTime(info.cancelledAt)}</div>
        <div><span className="font-bold">Source:</span> {info.source || "ADMIN"}</div>
        <div><span className="font-bold">Refund ID:</span> {info.refundId}</div>
        <div><span className="font-bold">Payment ID:</span> {info.paymentId}</div>
        <div><span className="font-bold">Refund Status:</span> {normalizeStatus(info.refundStatus)}</div>
        <div><span className="font-bold">Refund Amount:</span> {info.refundAmountRupees == null ? "-" : fmtMoney(info.refundAmountRupees)}</div>
        <div><span className="font-bold">Refund Speed:</span> {normalizeStatus(info.speed)}</div>
        <div><span className="font-bold">Refund Created:</span> {fmtAnyDateTime(info.refundCreatedAt)}</div>
        <div><span className="font-bold">Stock Rollback:</span> {stockDone ? `Done (${fmtQty(stockQty)} qty, ${batchCount} batch)` : "-"}</div>
        <div><span className="font-bold">Delivery Rollback:</span> {dispatchCancelled ? "Task cancelled" : "-"}</div>
      </div>
    </div>
  );
}



function StatCard({ title, value, icon, hint }) {
  return (
    <div className="rounded-[16px] border border-[#f2c6cc] bg-white p-3.5 shadow-[0_10px_20px_rgba(216,106,120,0.05)]">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[12px] font-semibold uppercase tracking-[0.08em] text-slate-500">
            {title}
          </p>
          <h3 className="mt-2 text-[22px] font-bold text-slate-800">{value}</h3>
          {hint ? (
            <p className="mt-1 text-[12px] text-slate-400">{hint}</p>
          ) : null}
        </div>
        <div className="grid h-10 w-10 place-items-center rounded-[12px] bg-[#fff6f7] text-[#d86a78]">
          {icon}
        </div>
      </div>
    </div>
  );
}

function buildRowsPerPageOptions(totalItems, currentLimit) {
  const total = Number(totalItems || 0);
  const current = Number(currentLimit || PAGE_SIZE);

  if (total <= 0) return [current || PAGE_SIZE];

  const options = [10, 25, 50, 100, 250, 500].filter((value) => value < total);
  options.push(total);

  return Array.from(new Set(options))
    .filter((value) => Number.isFinite(value) && value > 0)
    .sort((a, b) => a - b);
}

function Pagination({
  page,
  totalPages,
  totalItems,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
}) {
  const safePage = Math.max(1, Number(page || 1));
  const safeTotalPages = Math.max(1, Number(totalPages || 1));
  const safeTotalItems = Math.max(0, Number(totalItems || 0));
  const safeRowsPerPage = Math.max(1, Number(rowsPerPage || PAGE_SIZE));

  const start = safeTotalItems === 0 ? 0 : (safePage - 1) * safeRowsPerPage + 1;
  const end = safeTotalItems === 0 ? 0 : Math.min(safePage * safeRowsPerPage, safeTotalItems);
  const rowOptions = buildRowsPerPageOptions(safeTotalItems, safeRowsPerPage);

  return (
    <div className="mt-4 flex flex-col gap-3 rounded-[18px] border border-[#f2c6cc] bg-white px-4 py-3 shadow-[0_10px_25px_rgba(216,106,120,0.04)] sm:flex-row sm:items-center sm:justify-between">
      <div className="text-[13px] font-medium text-slate-500">
        Showing {start}-{end} of {safeTotalItems}
      </div>

      <div className="flex flex-wrap items-center gap-3 sm:justify-end">
        <label className="flex items-center gap-2 text-[12px] font-medium text-slate-500">
          Rows
          <select
            value={safeRowsPerPage}
            onChange={(event) => onRowsPerPageChange(Number(event.target.value || PAGE_SIZE))}
            className="h-9 rounded-[10px] border border-[#f2c6cc] bg-white px-3 text-[13px] font-medium text-slate-700 outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f9dfe4]"
          >
            {rowOptions.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </label>

        <button
          type="button"
          disabled={safePage <= 1}
          onClick={() => onPageChange(safePage - 1)}
          className="h-9 rounded-[10px] border border-[#f2c6cc] px-4 text-[12px] font-semibold text-slate-600 transition hover:bg-[#fff8f9] hover:text-[#d86a78] disabled:cursor-not-allowed disabled:opacity-45"
        >
          Prev
        </button>

        <span className="min-w-[76px] text-center text-[13px] font-semibold text-slate-500">
          Page {safePage} / {safeTotalPages}
        </span>

        <button
          type="button"
          disabled={safePage >= safeTotalPages}
          onClick={() => onPageChange(safePage + 1)}
          className="h-9 rounded-[10px] border border-[#f2c6cc] px-4 text-[12px] font-semibold text-slate-600 transition hover:bg-[#fff8f9] hover:text-[#d86a78] disabled:cursor-not-allowed disabled:opacity-45"
        >
          Next
        </button>
      </div>
    </div>
  );
}


function canChangeDriverForOrder(order = {}) {
  const delivery = order?.delivery || {};
  const currentStatus = String(order?.status || "").trim().toUpperCase();
  const paymentStatus = getPaymentStatusValue(order);
  const taskStatus = String(delivery?.taskStatus || "").toUpperCase();

  return !(
    ["CANCELLED", "DELIVERED"].includes(currentStatus) ||
    ["REFUNDED", "FAILED"].includes(paymentStatus) ||
    !isWarehouseOrderPaymentUnlocked(order) ||
    delivery?.pickedUpAt ||
    ["PICKED_UP", "OUT_FOR_DELIVERY", "DELIVERED", "CANCELLED"].includes(taskStatus)
  );
}


function canCancelWarehouseOrder(order = {}) {
  const currentStatus = getOrderStatusValue(order);
  const paymentStatus = getPaymentStatusValue(order);
  const delivery = order?.delivery || {};
  const taskStatus = String(delivery?.taskStatus || delivery?.task_status || '').trim().toUpperCase();
  const isPodPending = isPayOnDeliveryOrder(order) && paymentStatus === 'PAYMENT_PENDING_ON_DELIVERY';

  if (['CANCELLED', 'DELIVERED'].includes(currentStatus) || ['CANCELLED', 'DELIVERED'].includes(taskStatus)) {
    return false;
  }

  if (isPodPending) {
    return true;
  }

  // Online-paid orders can be cancelled + refunded at any point until successful delivery.
  // Pay on Delivery eligibility above remains unchanged.
  return !isPayOnDeliveryOrder(order) && paymentStatus === 'PAID';
}

function hasAssignedDriver(order = {}) {
  if (isWarehouseOrderTerminal(order)) return false;

  const delivery = order?.delivery || {};
  const taskStatus = String(delivery?.taskStatus || "").toUpperCase();

  return Boolean(
    delivery?.partner ||
    delivery?.partnerId ||
    delivery?.partner_id ||
    ["ASSIGNED", "PICKUP_PENDING", "PICKED_UP", "OUT_FOR_DELIVERY", "DELIVERED"].includes(taskStatus)
  );
}

function getWarehouseOrderActions(order = {}) {
  const currentStatus = getOrderStatusValue(order);
  const taskStatus = String(order?.delivery?.taskStatus || "").trim().toUpperCase();

  if (isWarehouseOrderTerminal(order)) {
    return [];
  }

  const lockedStatuses = new Set([
    "READY_FOR_DISPATCH",
    "PICKUP_PENDING",
    "PICKED_UP",
    "OUT_FOR_DELIVERY",
    "DELIVERED",
    "CANCELLED",
    "PAYMENT_FAILED",
  ]);

  if (lockedStatuses.has(currentStatus) || lockedStatuses.has(taskStatus)) {
    return [];
  }

  if (!isWarehouseOrderPaymentUnlocked(order)) {
    return [];
  }

  if (!hasAssignedDriver(order)) {
    return [];
  }

  return [{ key: "READY_FOR_DISPATCH", label: "READY FOR DISPATCH" }];
}

function formatShortDurationFromSeconds(seconds) {
  const totalSeconds = Number(seconds);
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return "-";

  if (totalSeconds < 60) {
    const rounded = Math.max(1, Math.round(totalSeconds));
    return `${rounded} ${rounded === 1 ? "second" : "seconds"}`;
  }

  const minutes = Math.round(totalSeconds / 60);
  if (minutes < 60) return `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? "hour" : "hours"}`;

  const days = Math.round(hours / 24);
  return `${days} ${days === 1 ? "day" : "days"}`;
}

function formatDriverReason(reason) {
  if (!reason) return "Not assignable";

  return String(reason).replace(/\((\d+)s old\)/gi, (_, seconds) => {
    return `(${formatShortDurationFromSeconds(seconds)} old)`;
  });
}

function formatDriverReasons(reasons) {
  if (!Array.isArray(reasons) || !reasons.length) return "Not assignable";
  return reasons.map(formatDriverReason).filter(Boolean).join(", ");
}

function getWarehouseActionHint(order = {}) {
  const currentStatus = getOrderStatusValue(order);
  const paymentStatus = getPaymentStatusValue(order);
  const taskStatus = String(order?.delivery?.taskStatus || "").trim().toUpperCase();

  if (isPaymentFailedOrder(order)) {
    return "Payment failed. Order closed";
  }

  if (isCancelledRefundedOrder(order) || ["DELIVERED", "REJECTED"].includes(currentStatus)) {
    return "Order closed";
  }

  if (taskStatus === "CANCELLED") {
    return "Dispatch cancelled. Cancel/refund still pending";
  }

  if (taskStatus === "DELIVERED") {
    return "Order closed";
  }

  if (!isWarehouseOrderPaymentUnlocked(order)) {
    const gatewayStatus = getGatewayStatusValue(order);
    if (gatewayStatus === "AUTHORIZED") {
      return "Payment authorized. Waiting for Razorpay capture or refund";
    }
    if (isUnclearPendingOrder(order)) {
      return "Payment still unclear. Verify + close is available";
    }
    if (paymentStatus === "PENDING" || currentStatus === "PAYMENT_PENDING") {
      return "Waiting for payment";
    }
    return paymentStatus ? `Payment ${normalizeStatus(paymentStatus).toLowerCase()}. Order closed` : "Payment not confirmed";
  }

  if (currentStatus === "READY_FOR_DISPATCH" || taskStatus === "PICKUP_PENDING") {
    return "Ready for dispatch. Waiting for driver pickup";
  }

  if (["PICKED_UP", "OUT_FOR_DELIVERY"].includes(currentStatus) || ["PICKED_UP", "OUT_FOR_DELIVERY"].includes(taskStatus)) {
    if (!isPayOnDeliveryOrder(order) && paymentStatus === 'PAID') {
      return 'Driver picked up. Cancel + refund is still available until delivery';
    }
    return "Driver already picked this order";
  }

  if (!hasAssignedDriver(order)) {
    return "Waiting for driver assignment";
  }

  return "";
}

function ManualAssignModal({
  open,
  order,
  options,
  loading,
  assigningId,
  showUnavailable,
  message,
  onClose,
  onToggleUnavailable,
  onAssign,
}) {
  if (!open || !order) return null;

  const assignablePartners = Array.isArray(options?.assignablePartners)
    ? options.assignablePartners
    : [];
  const unavailablePartners = Array.isArray(options?.unavailablePartners)
    ? options.unavailablePartners
    : [];
  const canChange = options?.canChangeAssignedPartner !== false && canChangeDriverForOrder(order);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[90vh] w-full max-w-5xl overflow-hidden rounded-[24px] bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-[#f8dde1] px-6 py-4">
          <div>
            <h3 className="text-[18px] font-bold text-slate-800">
              Manual Driver Assignment
            </h3>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <WarehouseDailyNumberBadge order={order} compact />
              <p className="text-[13px] font-semibold text-slate-500">
                {getReadableCustomerOrderId(order)} • Radius {options?.radiusKm || "-"} km
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-[10px] p-2 text-slate-500 hover:bg-slate-100"
          >
            <X size={18} />
          </button>
        </div>

        <div className="max-h-[72vh] overflow-y-auto p-6">
          {message ? (
            <div className="mb-4 rounded-[14px] border border-[#f2c6cc] bg-[#fff8f9] px-4 py-3 text-[13px] font-semibold text-[#d86a78]">
              {message}
            </div>
          ) : null}

          {!canChange ? (
            <div className="rounded-[14px] border border-amber-200 bg-amber-50 px-4 py-3 text-[14px] font-semibold text-amber-700">
              Driver change is locked because the rider has already picked up the order from the warehouse or the task is closed.
            </div>
          ) : null}

          <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h4 className="text-[15px] font-bold text-slate-800">
                Live Redis / nearby assignable riders
              </h4>
              <p className="mt-1 text-[13px] text-slate-500">
                Assignable riders are shown first. Unavailable riders include the exact reason so auto-assign failures can be debugged fast.
              </p>
            </div>

            <button
              type="button"
              disabled={loading}
              onClick={onToggleUnavailable}
              className="rounded-[12px] border border-[#f2c6cc] px-4 py-2 text-[12px] font-semibold text-slate-600 hover:bg-[#fff8f9] disabled:opacity-60"
            >
              {showUnavailable ? "Hide unavailable riders" : "Show unavailable riders + reasons"}
            </button>
          </div>

          {loading ? (
            <div className="rounded-[14px] border border-[#f2c6cc] px-4 py-8 text-center text-[14px] text-slate-500">
              Loading riders...
            </div>
          ) : assignablePartners.length ? (
            <div className="grid gap-3 md:grid-cols-2">
              {assignablePartners.map((partner) => (
                <div
                  key={partner.id}
                  className="rounded-[16px] border border-[#f2c6cc] p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-bold text-slate-800">{partner.name || "-"}</div>
                      <div className="mt-1 text-[12px] text-slate-500">
                        {partner.phone || "-"} • {partner.partnerId || partner.id}
                      </div>
                      <div className="mt-1 text-[12px] text-slate-500">
                        {[partner.vehicleType, partner.vehicleNumber].filter(Boolean).join(" / ") || "-"}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2 text-[11px] font-semibold">
                        <span className="rounded-full bg-emerald-50 px-2 py-1 text-emerald-700">
                          Online
                        </span>
                        <span className="rounded-full bg-[#fff6f7] px-2 py-1 text-[#d86a78]">
                          Active {partner.activeOrderCount}/{partner.maxActiveOrders}
                        </span>
                        <span className="rounded-full bg-slate-50 px-2 py-1 text-slate-600">
                          {partner.pickupDistanceKm == null
                            ? "-"
                            : `${Number(partner.pickupDistanceKm).toFixed(2)} km`}
                        </span>
                      </div>
                    </div>

                    <button
                      type="button"
                      disabled={!canChange || assigningId === partner.id}
                      onClick={() => onAssign(partner.id)}
                      className="rounded-[12px] bg-[#d86a78] px-4 py-2 text-[12px] font-bold text-white disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {assigningId === partner.id ? "Assigning..." : "Assign"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-[14px] border border-amber-200 bg-amber-50 px-4 py-4 text-[14px] font-semibold text-amber-700">
              No rider is assignable right now. Check unavailable riders below for the exact reason: outside pickup radius, stale location, active order limit, or food pickup lock.
            </div>
          )}

          {showUnavailable ? (
            <div className="mt-6">
              <h4 className="text-[15px] font-bold text-slate-800">
                Other approved riders
              </h4>
              <p className="mt-1 text-[13px] text-slate-500">
                These riders are blocked for manual assignment too. Reasons are the same rules used by auto-assign.
              </p>

              <div className="mt-3 grid gap-3 md:grid-cols-2">
                {unavailablePartners.length ? (
                  unavailablePartners.map((partner) => (
                    <div
                      key={partner.id}
                      className="rounded-[16px] border border-slate-200 bg-slate-50 p-4 opacity-90"
                    >
                      <div className="font-bold text-slate-700">{partner.name || "-"}</div>
                      <div className="mt-1 text-[12px] text-slate-500">
                        {partner.phone || "-"} • {partner.partnerId || partner.id}
                      </div>
                      <div className="mt-2 text-[12px] font-semibold text-rose-600">
                        {formatDriverReasons(partner.reasons)}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="rounded-[14px] border border-slate-200 bg-slate-50 px-4 py-4 text-[13px] text-slate-500">
                    No unavailable riders found.
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}


function toNum(value) {
  if (value === undefined || value === null || value === "") return null;
  const out = Number(value);
  return Number.isFinite(out) ? out : null;
}

function hasLatLng(point = {}) {
  return toNum(point?.lat) !== null && toNum(point?.lng) !== null;
}

function getPointLabel(point = {}) {
  return [
    point?.name,
    point?.address?.addressLine1,
    point?.address?.addressLine2,
    point?.address?.city,
    point?.address?.pincode,
  ]
    .filter(Boolean)
    .join(", ");
}




function buildGoogleDirectionsUrl({ from, to }) {
  const fromLat = toNum(from?.lat);
  const fromLng = toNum(from?.lng);
  const toLat = toNum(to?.lat);
  const toLng = toNum(to?.lng);

  if (fromLat === null || fromLng === null || toLat === null || toLng === null) {
    return "";
  }

  return `https://www.google.com/maps/dir/?api=1&origin=${fromLat},${fromLng}&destination=${toLat},${toLng}`;
}

function getTrackingStatus(tracking = {}) {
  return String(tracking?.task?.status || "").trim().toUpperCase();
}

function shouldAutoRefreshTracking(tracking = {}) {
  const status = getTrackingStatus(tracking);
  return Boolean(tracking?.trackingAvailable && LIVE_TRACKING_STATUSES.has(status));
}

function LiveGoogleTrackingMap({ driverLocation, pickupLocation, dropLocation, orderId = null }) {
  const [directions, setDirections] = useState(null);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: env.GOOGLE_MAPS_API_KEY,
  });

  const driverPos = hasLatLng(driverLocation)
    ? {
      lat: Number(driverLocation.lat),
      lng: Number(driverLocation.lng),
    }
    : null;

  const pickupPos = hasLatLng(pickupLocation)
    ? {
      lat: Number(pickupLocation.lat),
      lng: Number(pickupLocation.lng),
    }
    : null;

  const dropPos = hasLatLng(dropLocation)
    ? {
      lat: Number(dropLocation.lat),
      lng: Number(dropLocation.lng),
    }
    : null;

  const center = driverPos || dropPos || pickupPos || {
    lat: 28.6139,
    lng: 77.209,
  };

  const driverBikeIcon =
    isLoaded && window.google?.maps
      ? {
        url: "/map-icons/driver-bike.png",
        scaledSize: new window.google.maps.Size(48, 48),
        anchor: new window.google.maps.Point(24, 24),
      }
      : undefined;

  useEffect(() => {
    if (!isLoaded || !driverPos || !dropPos || !window.google?.maps) {
      setDirections(null);
      return;
    }

    const service = new window.google.maps.DirectionsService();
    const startedAt = Date.now();

    service.route(
      {
        origin: driverPos,
        destination: dropPos,
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        void ReportsService.trackGoogleClientUsage({
          eventId: `admin-directions-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`,
          occurredAt: new Date().toISOString(),
          apiFamily: "DIRECTIONS",
          apiMethod: "DirectionsService.route",
          purpose: "WAREHOUSE_ORDER_TRACKING_DIRECTIONS",
          credentialAlias: "ADMIN_GOOGLE_MAPS_KEY",
          orderId,
          attempt: 1,
          httpStatus: status === "OK" ? 200 : null,
          providerStatus: String(status || "UNKNOWN"),
          success: status === "OK" && Boolean(result),
          latencyMs: Date.now() - startedAt,
          billableUnits: 1,
        }).catch(() => {});

        if (status === "OK" && result) {
          setDirections(result);
        } else {
          setDirections(null);
        }
      }
    );
  }, [
    isLoaded,
    driverPos?.lat,
    driverPos?.lng,
    dropPos?.lat,
    dropPos?.lng,
    orderId,
  ]);

  if (!env.GOOGLE_MAPS_API_KEY) {
    return (
      <div className="flex h-[430px] items-center justify-center px-5 text-center text-[14px] font-semibold text-amber-700">
        Google Maps API key missing. Please add REACT_APP_GOOGLE_MAPS_API_KEY in .env.
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex h-[430px] items-center justify-center px-5 text-center text-[14px] font-semibold text-rose-700">
        Google Map could not be loaded. Please check the API key or Maps JavaScript API.
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="flex h-[430px] items-center justify-center px-5 text-center text-[14px] font-semibold text-[#d86a78]">
        Loading Google Map...
      </div>
    );
  }

  return (
    <GoogleMap
  mapContainerStyle={{
    width: "100%",
    height: "430px",
  }}
  center={center}
  zoom={16}
  onLoad={(map) => {
    if (!window.google?.maps) return;

    void ReportsService.trackGoogleClientUsage({
      eventId: `admin-map-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`,
      occurredAt: new Date().toISOString(),
      apiFamily: "MAPS_JAVASCRIPT",
      apiMethod: "mapLoad",
      purpose: "WAREHOUSE_ORDER_TRACKING_MAP",
      credentialAlias: "ADMIN_GOOGLE_MAPS_KEY",
      orderId,
      attempt: 1,
      httpStatus: 200,
      providerStatus: "OK",
      success: true,
      latencyMs: null,
      billableUnits: 1,
    }).catch(() => {});

    const bounds = new window.google.maps.LatLngBounds();

    if (pickupPos) bounds.extend(pickupPos);
    if (dropPos) bounds.extend(dropPos);
    if (driverPos) bounds.extend(driverPos);

    if (!bounds.isEmpty()) {
      map.fitBounds(bounds);

      window.setTimeout(() => {
        const currentZoom = map.getZoom();

        if (!currentZoom || currentZoom > 17) {
          map.setZoom(17);
        }

        if (currentZoom && currentZoom < 14) {
          map.setZoom(14);
        }
      }, 300);
    }
  }}
  options={{
    streetViewControl: false,
    mapTypeControl: false,
    fullscreenControl: true,
    clickableIcons: false,
    gestureHandling: "greedy",
  }}
>
      {directions ? (
       <DirectionsRenderer
  options={{
    directions,
    suppressMarkers: true,
    preserveViewport: true,
  }}
/>
      ) : null}

      {pickupPos ? (
        <MarkerF
          position={pickupPos}
          label="W"
          title="Warehouse"
        />
      ) : null}

      {dropPos ? (
        <MarkerF
          position={dropPos}
          label="C"
          title="Customer"
        />
      ) : null}

      {driverPos ? (
        <MarkerF
          position={driverPos}
          icon={driverBikeIcon}
          title="Driver"
        />
      ) : null}
    </GoogleMap>
  );
}



function TrackingInfoCard({ title, icon, children }) {
  return (
    <div className="rounded-[16px] border border-[#f2c6cc] bg-white p-4">
      <div className="mb-3 flex items-center gap-2 text-[13px] font-bold text-slate-700">
        {icon}
        {title}
      </div>
      <div className="space-y-1 text-[13px] text-slate-600">{children}</div>
    </div>
  );
}

function OrderTrackingMapModal({
  open,
  order,
  tracking,
  loading,
  refreshing,
  lastFetchedAt,
  message,
  onClose,
  onRefresh,
}) {
  if (!open) return null;

  const driverLocation = tracking?.location || null;
  const pickupLocation = tracking?.pickup || null;
  const dropLocation = tracking?.drop || order?.address || null;

  const liveTracking = shouldAutoRefreshTracking(tracking);

  const directionsUrl = buildGoogleDirectionsUrl({
    from: driverLocation,
    to: dropLocation,
  });

  const canShowAnyMap =
    hasLatLng(driverLocation) || hasLatLng(dropLocation) || hasLatLng(pickupLocation);



  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[92vh] w-full max-w-6xl overflow-hidden rounded-[24px] bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-[#f8dde1] px-6 py-4">
          <div>
            <h3 className="flex items-center gap-2 text-[18px] font-bold text-slate-800">
              <MapPin size={19} className="text-[#d86a78]" />
              Delivery Live Tracking
            </h3>
            <p className="mt-1 text-[13px] font-semibold text-slate-500">
              {getReadableCustomerOrderId(order)} • Warehouse panel order tracking
            </p>
          </div>

          <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
  <span
    className={[
      "inline-flex h-9 min-w-[96px] items-center justify-center rounded-full border px-4 text-[12px] font-bold leading-none shadow-sm",
      liveTracking
        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
        : "border-slate-200 bg-slate-50 text-slate-500",
    ].join(" ")}
  >
    {liveTracking ? "Live moving" : "Static"}
  </span>

  <span className="inline-flex h-9 min-w-[220px] items-center justify-center rounded-full border border-slate-200 bg-slate-50 px-4 text-[12px] font-bold leading-none text-slate-500 shadow-sm">
    {lastFetchedAt ? `Updated ${fmtDateTime(lastFetchedAt)}` : "Updated -"}
  </span>

  <button
    type="button"
    disabled={loading || refreshing}
    onClick={onRefresh}
    className="inline-flex h-9 min-w-[102px] items-center justify-center gap-2 rounded-full border border-[#f2c6cc] bg-white px-4 text-[12px] font-bold leading-none text-slate-600 shadow-sm transition hover:bg-[#fff8f9] hover:text-[#d86a78] disabled:cursor-not-allowed disabled:opacity-60"
  >
    {refreshing ? (
      <>
        <Loader2 size={14} className="animate-spin" />
        Refreshing
      </>
    ) : (
      <>
        <RefreshCcw size={14} />
        Refresh
      </>
    )}
  </button>
</div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-[10px] p-2 text-slate-500 hover:bg-slate-100"
          >
            <X size={18} />
          </button>
        </div>

        <div className="max-h-[78vh] overflow-y-auto p-6">
          {loading ? (
            <div className="flex min-h-[360px] items-center justify-center rounded-[18px] border border-[#f2c6cc] bg-[#fff8f9]">
              <div className="flex items-center gap-3 text-[14px] font-semibold text-[#d86a78]">
                <Loader2 size={18} className="animate-spin" />
                Loading live tracking...
              </div>
            </div>
          ) : message ? (
            <div className="rounded-[16px] border border-amber-200 bg-amber-50 px-4 py-4 text-[14px] font-semibold text-amber-700">
              {message}
            </div>
          ) : (
            <>
              <div className="grid items-start gap-4 lg:grid-cols-[1fr_360px]">
               <div className="self-start overflow-hidden rounded-[18px] border border-[#f2c6cc] bg-white">
                  {canShowAnyMap ? (
                    <LiveGoogleTrackingMap
                      driverLocation={driverLocation}
                      pickupLocation={pickupLocation}
                      dropLocation={dropLocation}
                      orderId={order?.id || order?.uuid || order?.orderId || order?.order_id || null}
                    />
                  ) : (
                    <div className="flex h-[430px] items-center justify-center px-5 text-center text-[14px] font-semibold text-slate-500">
                     Driver/customer latitude and longitude are not available yet.
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <TrackingInfoCard
                    title="Driver"
                    icon={<Truck size={15} className="text-[#d86a78]" />}
                  >
                    <div><span className="font-semibold">Name:</span> {tracking?.driver?.name || "-"}</div>
                    <div><span className="font-semibold">Phone:</span> {tracking?.driver?.phone || "-"}</div>
                    <div>
                      <span className="font-semibold">Vehicle:</span>{" "}
                      {[tracking?.driver?.vehicleType, tracking?.driver?.vehicleNumber].filter(Boolean).join(" / ") || "-"}
                    </div>
                    <div><span className="font-semibold">Online:</span> {tracking?.driver?.isOnline ? "Yes" : "No"}</div>
                    <div><span className="font-semibold">Available:</span> {tracking?.driver?.isAvailable ? "Yes" : "No"}</div>
                  </TrackingInfoCard>

                  <TrackingInfoCard
                    title="Live Location"
                    icon={<MapPin size={15} className="text-[#d86a78]" />}
                  >
                    <div><span className="font-semibold">Lat:</span> {toNum(driverLocation?.lat) ?? "-"}</div>
                    <div><span className="font-semibold">Lng:</span> {toNum(driverLocation?.lng) ?? "-"}</div>
                    <div><span className="font-semibold">Last seen:</span> {fmtDateTime(driverLocation?.lastSeenAt)}</div>
                    <div><span className="font-semibold">Source:</span> {driverLocation?.source || "-"}</div>
                  </TrackingInfoCard>

                  <TrackingInfoCard
                    title="Task"
                    icon={<PackageCheck size={15} className="text-[#d86a78]" />}
                  >
                    <div>
                      <span className="font-semibold">Task status:</span>{" "}
                      {tracking?.task?.status ? normalizeStatus(tracking.task.status) : "-"}
                    </div>
                    <div>
                      <span className="font-semibold">Assignment:</span>{" "}
                      {tracking?.task?.assignmentStatus ? normalizeStatus(tracking.task.assignmentStatus) : "-"}
                    </div>
                    <div><span className="font-semibold">Updated:</span> {fmtDateTime(tracking?.task?.updatedAt)}</div>
                  </TrackingInfoCard>
                </div>
              </div>

              <div className="mt-4 grid gap-4 lg:grid-cols-2">
                <TrackingInfoCard
                  title="Pickup"
                  icon={<MapPin size={15} className="text-[#d86a78]" />}
                >
                  <div>{getPointLabel(pickupLocation) || "-"}</div>
                  <div>
                    <span className="font-semibold">Lat/Lng:</span>{" "}
                    {hasLatLng(pickupLocation) ? `${pickupLocation.lat}, ${pickupLocation.lng}` : "-"}
                  </div>
                </TrackingInfoCard>

                <TrackingInfoCard
                  title="Drop / Customer"
                  icon={<MapPin size={15} className="text-[#d86a78]" />}
                >
                  <div>{getPointLabel(dropLocation) || "-"}</div>
                  <div>
                    <span className="font-semibold">Lat/Lng:</span>{" "}
                    {hasLatLng(dropLocation) ? `${dropLocation.lat}, ${dropLocation.lng}` : "-"}
                  </div>
                </TrackingInfoCard>
              </div>

              <div className="mt-5 flex flex-wrap justify-end gap-3">
                {directionsUrl ? (
                  <button
                    type="button"
                    onClick={() => window.open(directionsUrl, "_blank", "noopener,noreferrer")}
                    className="inline-flex items-center gap-2 rounded-[12px] bg-[#d86a78] px-4 py-2 text-[13px] font-bold text-white"
                  >
                    <Navigation size={15} />
                    Open Directions
                  </button>
                ) : null}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function RowStatusActions({ order, currentStatus, loading, onChange }) {
  const normalizedCurrentStatus = String(currentStatus || "").trim().toUpperCase();
  const actions = getWarehouseOrderActions(order);
  const hint = getWarehouseActionHint(order);

  if (!actions.length) {
    return (
      <div className="max-w-[260px] rounded-[12px] border border-[#f8dde1] bg-[#fff8f9] px-3 py-2 text-[11px] font-semibold text-slate-500">
        {hint || normalizeStatus(normalizedCurrentStatus)}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-1.5 max-w-[260px]">
      {actions.map((status) => {
        const active = normalizedCurrentStatus === status.key;

        return (
          <button
            key={status.key}
            type="button"
            disabled={loading || active}
            onClick={(e) => {
              e.stopPropagation();
              onChange(status.key);
            }}
            className={[
              "rounded-[10px] px-3 py-1.5 text-[11px] font-semibold transition",
              active
                ? "bg-[#d86a78] text-white"
                : "bg-white text-slate-700 border border-rose-100 hover:border-[#d86a78] hover:text-[#d86a78]",
              loading ? "opacity-60 cursor-not-allowed" : "",
            ].join(" ")}
          >
            {loading ? "Updating..." : status.label}
          </button>
        );
      })}
    </div>
  );
}

export default function WarehouseCustomerOrders() {
  const [loading, setLoading] = useState(false);
  const [pageError, setPageError] = useState("");

  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState({
    totalOrders: 0,
    activeOrders: 0,
    deliveredOrders: 0,
    cancelledOrders: 0,
    totalSales: 0,
    averageOrderValue: 0,
    totalDeliveryFee: 0,
  });
  const [statusFilter, setStatusFilter] = useState("");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("");
  const [paymentMethodFilter, setPaymentMethodFilter] = useState("");
  const [dateFilterMode, setDateFilterMode] = useState("30d");
  const [customFromDate, setCustomFromDate] = useState("");
  const [customToDate, setCustomToDate] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(PAGE_SIZE);
  const [meta, setMeta] = useState({
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
  });
  const latestOrdersRequestRef = useRef(0);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [rowStatusLoadingId, setRowStatusLoadingId] = useState("");
  const [message, setMessage] = useState("");
  const [manualAssignOpen, setManualAssignOpen] = useState(false);
  const [manualAssignOrder, setManualAssignOrder] = useState(null);
  const [manualAssignOptions, setManualAssignOptions] = useState(null);
  const [manualAssignLoading, setManualAssignLoading] = useState(false);
  const [manualAssigningId, setManualAssigningId] = useState("");
  const [manualAssignShowUnavailable, setManualAssignShowUnavailable] = useState(false);
  const [manualAssignMessage, setManualAssignMessage] = useState("");
  const [trackingOpen, setTrackingOpen] = useState(false);
  const [trackingOrder, setTrackingOrder] = useState(null);
  const [trackingData, setTrackingData] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingMessage, setTrackingMessage] = useState("");
  const [trackingRefreshing, setTrackingRefreshing] = useState(false);
  const [trackingLastFetchedAt, setTrackingLastFetchedAt] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelMode, setCancelMode] = useState("refund");
  const [cancelLoading, setCancelLoading] = useState(false);

  const activeDateFilters = useMemo(() => {
    if (dateFilterMode === "all") {
      return { fromDate: "", toDate: "" };
    }

    if (dateFilterMode === "custom") {
      return {
        fromDate: customFromDate,
        toDate: customToDate,
      };
    }

    return getLastThirtyDaysRange();
  }, [dateFilterMode, customFromDate, customToDate]);

  const invalidCustomDateRange =
    dateFilterMode === "custom" &&
    customFromDate &&
    customToDate &&
    customFromDate > customToDate;

  const dateRangeLabel = useMemo(() => {
    if (dateFilterMode === "all") return "All time";
    if (dateFilterMode === "custom") {
      if (customFromDate && customToDate) {
        return `${fmtDate(customFromDate)} - ${fmtDate(customToDate)}`;
      }
      if (customFromDate) return `From ${fmtDate(customFromDate)}`;
      if (customToDate) return `Until ${fmtDate(customToDate)}`;
      return "Custom date";
    }
    return "Last 30 days";
  }, [dateFilterMode, customFromDate, customToDate]);


  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 450);

    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [
    statusFilter,
    paymentStatusFilter,
    paymentMethodFilter,
    dateFilterMode,
    customFromDate,
    customToDate,
    debouncedSearch,
    rowsPerPage,
  ]);

  useEffect(() => {
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    statusFilter,
    paymentStatusFilter,
    paymentMethodFilter,
    dateFilterMode,
    customFromDate,
    customToDate,
    debouncedSearch,
    rowsPerPage,
  ]);

  useEffect(() => {
    const total = Number(meta.total || 0);
    if (total > 0 && rowsPerPage > total) {
      setRowsPerPage(total);
      setPage(1);
    }
  }, [meta.total, rowsPerPage]);

  async function loadOrders({ silent = false } = {}) {
    const requestId = latestOrdersRequestRef.current + 1;
    latestOrdersRequestRef.current = requestId;

    if (invalidCustomDateRange) {
      if (!silent) {
        setLoading(false);
        setPageError("From date cannot be after To date");
        setOrders([]);
        setMeta({ page: 1, limit: rowsPerPage, total: 0 });
        setSummary({
          totalOrders: 0,
          activeOrders: 0,
          deliveredOrders: 0,
          cancelledOrders: 0,
          totalSales: 0,
          averageOrderValue: 0,
          totalDeliveryFee: 0,
        });
      }
      return;
    }

    try {
      if (!silent) setLoading(true);
      setPageError("");

      const requestedFilters = {
        status: statusFilter,
        paymentStatus: paymentStatusFilter,
        paymentMethod: paymentMethodFilter,
        fromDate: activeDateFilters.fromDate,
        toDate: activeDateFilters.toDate,
        q: debouncedSearch,
      };

      const res = await warehouseInventoryService.listCustomerOrders({
        ...requestedFilters,
        page,
        limit: rowsPerPage,
      });

      // A slower response from an older filter selection must not overwrite
      // cards/table data from the latest selection.
      if (requestId !== latestOrdersRequestRef.current) return;

      setOrders(Array.isArray(res?.items) ? res.items : []);
      setMeta({
        page: Number(res?.meta?.page || page),
        limit: Number(res?.meta?.limit || rowsPerPage),
        total: Number(res?.meta?.total || 0),
      });
      setSummary({
        totalOrders: Number(res?.summary?.totalOrders ?? res?.meta?.total ?? 0),
        activeOrders: Number(res?.summary?.activeOrders || 0),
        deliveredOrders: Number(res?.summary?.deliveredOrders || 0),
        cancelledOrders: Number(res?.summary?.cancelledOrders || 0),
        totalSales: Number(res?.summary?.totalSales || 0),
        averageOrderValue: Number(res?.summary?.averageOrderValue || 0),
        totalDeliveryFee: Number(res?.summary?.totalDeliveryFee || 0),
      });
    } catch (err) {
      if (requestId !== latestOrdersRequestRef.current) return;

      if (!silent) {
        setPageError(getErrorMessage(err));
        setOrders([]);
        setMeta({
          page: 1,
          limit: rowsPerPage,
          total: 0,
        });
        setSummary({
          totalOrders: 0,
          activeOrders: 0,
          deliveredOrders: 0,
          cancelledOrders: 0,
          totalSales: 0,
          averageOrderValue: 0,
          totalDeliveryFee: 0,
        });
      }
    } finally {
      if (requestId === latestOrdersRequestRef.current && !silent) {
        setLoading(false);
      }
    }
  }

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      loadOrders({ silent: true });
    }, ORDER_LIST_REFRESH_MS);

    return () => window.clearInterval(timer);
    // The current filters/page are intentionally dependencies so every timer
    // refreshes the exact list the warehouse user is currently viewing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    statusFilter,
    paymentStatusFilter,
    paymentMethodFilter,
    dateFilterMode,
    customFromDate,
    customToDate,
    debouncedSearch,
    rowsPerPage,
  ]);

  async function openDetail(id) {
    try {
      setLoading(true);
      setPageError("");
      setMessage("");

      const res = await warehouseInventoryService.getCustomerOrder(id);
      setDetail({
        ...(res?.order || res || {}),
        items: res?.items || res?.order?.items || [],
      });
      setDetailOpen(true);
    } catch (err) {
      setPageError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  async function changeStatus(nextStatus) {
    try {
      if (!detail?.id || !nextStatus) return;

      setStatusUpdating(true);
      setMessage("");

      const res = await warehouseInventoryService.updateCustomerOrderStatus(
        detail.id,
        { status: nextStatus }
      );

      setDetail({
        ...(res?.order || res || {}),
        items: res?.items || res?.order?.items || detail?.items || [],
      });

      setMessage("Customer order status updated successfully");
      await loadOrders();
    } catch (err) {
      setMessage(getErrorMessage(err));
    } finally {
      setStatusUpdating(false);
    }
  }

  async function changeRowStatus(orderId, nextStatus) {
    try {
      if (!orderId || !nextStatus) return;

      setRowStatusLoadingId(orderId);
      setPageError("");

      await warehouseInventoryService.updateCustomerOrderStatus(orderId, {
        status: nextStatus,
      });

      if (detail?.id === orderId) {
        setDetail((prev) => ({
          ...(prev || {}),
          status: nextStatus,
        }));
      }

      await loadOrders();
    } catch (err) {
      setPageError(getErrorMessage(err));
    } finally {
      setRowStatusLoadingId("");
    }
  }


  function openCancelOrder(order, mode = "refund") {
    if (!order) return;
    setCancelTarget(order);
    setCancelMode(mode);
    setCancelReason("");
    setMessage("");
    setPageError("");
  }

  async function submitCancelOrder() {
    const orderId = cancelTarget?.id;
    const reason = cancelReason.trim();
    if (!orderId || reason.length < 3) {
      setMessage("Cancellation reason is required");
      return;
    }

    try {
      setCancelLoading(true);
      setMessage("");
      setPageError("");
      const res = cancelMode === "unclear"
        ? await orderPendingPaymentService.closeUnclearPendingOrder(orderId, {
            reason,
            refundSpeed: "normal",
          })
        : await warehouseInventoryService.cancelCustomerOrder(orderId, {
            reason,
            refundSpeed: "normal",
          });
      const updated = {
        ...(res?.order || res || {}),
        items: res?.items || res?.order?.items || cancelTarget?.items || [],
      };
      setCancelTarget(null);
      setCancelReason("");
      setCancelMode("refund");
      setMessage(res?.message || (cancelMode === "unclear" ? "Unclear pending order closed" : "Order cancelled and refund initiated"));
      if (detail?.id === orderId) setDetail(updated);
      await loadOrders();
      if (detailOpen && detail?.id === orderId) await openDetail(orderId);
    } catch (err) {
      setMessage(getErrorMessage(err));
      setPageError(getErrorMessage(err));
    } finally {
      setCancelLoading(false);
    }
  }


  async function loadManualAssignmentOptions(orderId, includeUnavailable = false) {
    try {
      setManualAssignLoading(true);
      setManualAssignMessage("");

      const res = await warehouseInventoryService.getCustomerOrderManualAssignmentOptions(
        orderId,
        { includeUnavailable }
      );

      setManualAssignOptions(res || null);
    } catch (err) {
      setManualAssignMessage(getErrorMessage(err));
      setManualAssignOptions(null);
    } finally {
      setManualAssignLoading(false);
    }
  }

  async function openManualAssign(order) {
    if (!order?.id) return;

    setManualAssignOrder(order);
    setManualAssignOpen(true);
    setManualAssignOptions(null);
    setManualAssignShowUnavailable(true);
    setManualAssignMessage("");

    await loadManualAssignmentOptions(order.id, true);
  }

  async function toggleUnavailablePartners() {
    const next = !manualAssignShowUnavailable;
    setManualAssignShowUnavailable(next);

    if (manualAssignOrder?.id) {
      await loadManualAssignmentOptions(manualAssignOrder.id, next);
    }
  }

  async function assignDriver(partnerId) {
    try {
      if (!manualAssignOrder?.id || !partnerId) return;

      setManualAssigningId(partnerId);
      setManualAssignMessage("");

      const res = await warehouseInventoryService.manualAssignCustomerOrder(
        manualAssignOrder.id,
        { partnerId }
      );

      if (res?.order) {
        setManualAssignOrder(res.order);
        if (detail?.id === res.order.id) {
          setDetail({
            ...(res.order || {}),
            items: res?.items || detail?.items || [],
          });
        }
      }

      setManualAssignMessage("Driver assigned successfully");
      await loadManualAssignmentOptions(manualAssignOrder.id, manualAssignShowUnavailable);
      await loadOrders();
    } catch (err) {
      setManualAssignMessage(getErrorMessage(err));
    } finally {
      setManualAssigningId("");
    }
  }


  async function fetchTracking(orderId, { silent = false } = {}) {
    if (!orderId) return;

    try {
      if (silent) {
        setTrackingRefreshing(true);
      } else {
        setTrackingLoading(true);
      }

      const res = await warehouseInventoryService.getWarehouseCustomerOrderTracking(orderId);

      if (res?.trackingAvailable === false) {
        setTrackingData(res || null);
        setTrackingMessage(res?.message || "Live tracking is not available for this order yet.");
        setTrackingLastFetchedAt(new Date());
        return;
      }

      setTrackingData(res || null);
      setTrackingMessage("");
      setTrackingLastFetchedAt(new Date());
    } catch (err) {
      if (!silent) {
        setTrackingData(null);
        setTrackingMessage(getErrorMessage(err));
      }
    } finally {
      if (silent) {
        setTrackingRefreshing(false);
      } else {
        setTrackingLoading(false);
      }
    }
  }

  async function openTracking(order) {
    if (!order?.id) return;

    setTrackingOrder(order);
    setTrackingOpen(true);
    setTrackingData(null);
    setTrackingMessage("");
    setTrackingLastFetchedAt(null);

    await fetchTracking(order.id, { silent: false });
  }

  useEffect(() => {
    if (!trackingOpen || !trackingOrder?.id) return;

    const status = getTrackingStatus(trackingData);
    const canAutoRefresh =
      Boolean(trackingData?.trackingAvailable) &&
      LIVE_TRACKING_STATUSES.has(status);

    if (!canAutoRefresh) return;

    const timer = window.setInterval(() => {
      fetchTracking(trackingOrder.id, { silent: true });
    }, TRACKING_REFRESH_MS);

    return () => window.clearInterval(timer);
  }, [
    trackingOpen,
    trackingOrder?.id,
    trackingData?.trackingAvailable,
    trackingData?.task?.status,
  ]);

  const currentPageLimit = Math.max(1, Number(meta.limit || rowsPerPage || PAGE_SIZE));
  const totalPages = Math.max(
    1,
    Math.ceil(Number(meta.total || 0) / currentPageLimit)
  );

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const stats = useMemo(
    () => ({
      totalOrders: Number(summary?.totalOrders || 0),
      activeOrders: Number(summary?.activeOrders || 0),
      deliveredOrders: Number(summary?.deliveredOrders || 0),
      cancelledOrders: Number(summary?.cancelledOrders || 0),
      totalSales: Number(summary?.totalSales || 0),
      averageOrderValue: Number(summary?.averageOrderValue || 0),
      totalDeliveryFee: Number(summary?.totalDeliveryFee || 0),
    }),
    [summary]
  );

  return (
    <div className="space-y-6">
      <div
        className="rounded-[24px] border bg-white p-6 shadow-[0_12px_30px_rgba(216,106,120,0.08)]"
        style={{ borderColor: BORDER }}
      >
        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-start gap-4">
            <div className="grid h-12 w-12 shrink-0 place-items-center rounded-[16px] bg-[#fff6f7] text-[#d86a78]">
              <PackageCheck size={22} />
            </div>

            <div>
              <h2 className="warehouse-page-title">
                Customer Orders
              </h2>
              <p className="mt-2 max-w-2xl text-[14px] leading-6 text-slate-500">
                View warehouse customer orders, track payment and delivery
                progress, open full order details, and update order status quickly.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={loadOrders}
            className="inline-flex items-center gap-2 rounded-[12px] border border-[#f2c6cc] px-4 py-2 text-[13px] font-semibold text-slate-600 transition hover:bg-[#fff8f9] hover:text-[#d86a78]"
          >
            <RefreshCcw size={16} />
            Refresh
          </button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-7">
        <StatCard
          title="Total Orders"
          value={stats.totalOrders}
          hint="Full selected range"
          icon={<ShoppingBag size={20} />}
        />

        <StatCard
          title="Total Sales"
          value={fmtMoney(stats.totalSales)}
          hint="Gross order value"
          icon={<IndianRupee size={20} />}
        />

        <StatCard
          title="Active Orders"
          value={stats.activeOrders}
          hint="Currently in progress"
          icon={<PackageCheck size={20} />}
        />

        <StatCard
          title="Delivered"
          value={stats.deliveredOrders}
          hint="Completed orders"
          icon={<Truck size={20} />}
        />

        <StatCard
          title="Cancelled"
          value={stats.cancelledOrders}
          hint="Cancelled orders"
          icon={<X size={20} />}
        />

        <StatCard
          title="Average Order"
          value={fmtMoney(stats.averageOrderValue)}
          hint="Average order value"
          icon={<CreditCard size={20} />}
        />

        <StatCard
          title="Delivery Fees"
          value={fmtMoney(stats.totalDeliveryFee)}
          hint="Total delivery fee"
          icon={<Truck size={20} />}
        />
      </div>

      {pageError ? (
        <div className="rounded-[16px] border border-[#f2c6cc] bg-[#fff8f9] px-4 py-3 text-[14px] font-medium text-[#d86a78]">
          {pageError}
        </div>
      ) : null}

      <div className="rounded-[20px] border border-[#f2c6cc] bg-white p-5 shadow-[0_10px_25px_rgba(216,106,120,0.05)]">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex flex-1 flex-col gap-3 md:flex-row md:flex-wrap">
            <select
              value={dateFilterMode}
              onChange={(e) => {
                const nextMode = e.target.value;
                setDateFilterMode(nextMode);

                if (nextMode === "custom" && !customFromDate && !customToDate) {
                  const range = getLastThirtyDaysRange();
                  setCustomFromDate(range.fromDate);
                  setCustomToDate(range.toDate);
                }
              }}
              className="h-[46px] min-w-[170px] rounded-[12px] border border-[#f2c6cc] px-4 text-[14px] outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f9dfe4]"
            >
              <option value="30d">Last 30 days</option>
              <option value="all">All time</option>
              <option value="custom">Custom date</option>
            </select>

            {dateFilterMode === "custom" ? (
              <>
                <input
                  type="date"
                  value={customFromDate}
                  max={customToDate || toLocalDateInput(new Date())}
                  onChange={(e) => setCustomFromDate(e.target.value)}
                  aria-label="Orders from date"
                  className="h-[46px] min-w-[160px] rounded-[12px] border border-[#f2c6cc] px-4 text-[14px] outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f9dfe4]"
                />
                <input
                  type="date"
                  value={customToDate}
                  min={customFromDate || undefined}
                  max={toLocalDateInput(new Date())}
                  onChange={(e) => setCustomToDate(e.target.value)}
                  aria-label="Orders to date"
                  className="h-[46px] min-w-[160px] rounded-[12px] border border-[#f2c6cc] px-4 text-[14px] outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f9dfe4]"
                />
              </>
            ) : null}

            <div className="relative w-full max-w-[420px]">
              <Search
                size={16}
                className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search order no, city, customer name"
                className="h-[46px] w-full rounded-[12px] border border-[#f2c6cc] pl-11 pr-4 text-[14px] outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f9dfe4]"
              />
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-[46px] min-w-[190px] rounded-[12px] border border-[#f2c6cc] px-4 text-[14px] outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f9dfe4]"
            >
              {ORDER_STATUS_FILTERS.map((item) => (
                <option key={item.value || "all-status"} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>

            <select
              value={paymentStatusFilter}
              onChange={(e) => setPaymentStatusFilter(e.target.value)}
              className="h-[46px] min-w-[190px] rounded-[12px] border border-[#f2c6cc] px-4 text-[14px] outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f9dfe4]"
            >
              {PAYMENT_STATUS_FILTERS.map((item) => (
                <option key={item.value || "all-payment-status"} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>

            <select
              value={paymentMethodFilter}
              onChange={(e) => setPaymentMethodFilter(e.target.value)}
              className="h-[46px] min-w-[170px] rounded-[12px] border border-[#f2c6cc] px-4 text-[14px] outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f9dfe4]"
            >
              {PAYMENT_METHOD_FILTERS.map((item) => (
                <option key={item.value || "all-payment-method"} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-3 text-[13px] text-slate-500">
            <span className="rounded-full bg-slate-50 px-3 py-2 font-semibold text-slate-600">
              {dateRangeLabel}
            </span>
            <span className="rounded-full bg-[#fff6f7] px-3 py-2 font-semibold text-[#d86a78]">
              {meta.total} orders
            </span>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-[22px] border border-[#f2c6cc] bg-white shadow-[0_12px_30px_rgba(216,106,120,0.08)]">
  <div className="overflow-x-auto">
    <table className="w-full min-w-[1120px] border-separate border-spacing-0 [&_thead_th]:border-b [&_thead_th]:border-[#f8dde1] [&_tbody_td]:border-t [&_tbody_td]:border-[#f8dde1]">
            <thead className="bg-[#fff7f8]">
              <tr className="text-left text-[13px] font-semibold text-slate-600">
                <th className="px-5 py-4">Order</th>
                <th className="px-5 py-4">Customer</th>
                <th className="px-5 py-4">Status</th>
                <th className="px-5 py-4">Payment</th>
                <th className="px-5 py-4">Items</th>
                <th className="px-5 py-4">Amount</th>
                <th className="px-5 py-4">Driver</th>
                <th className="px-5 py-4">Created</th>
                <th className="px-5 py-4 w-[220px]">Quick Update</th>
                <th className="px-4 py-4 text-center w-[170px]">Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={10}
                    className="px-5 py-10 text-center text-[14px] text-slate-500"
                  >
                    Loading customer orders...
                  </td>
                </tr>
              ) : orders.length ? (
                orders.map((item) => {
                  const pricing = getPricing(item);

                  return (
                   <tr
  key={item.id}
  onClick={() => openDetail(item.id)}
  className="cursor-pointer text-[14px] text-slate-700 transition hover:bg-[#fffafb]"
>
                      <td className="border-t border-[#f8dde1] px-5 py-4">
                        <div className="flex items-center gap-3">
                          <WarehouseDailyNumberBadge order={item} compact />
                          <div>
                            <div className="font-semibold text-slate-800">
                              {getReadableCustomerOrderId(item)}
                            </div>
                            {getWarehouseDailyNumber(item) ? (
                              <div className="mt-1 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">
                                Packet number
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </td>

                     <td className="border-t border-[#f8dde1] px-5 py-4">
                        <div className="font-semibold text-slate-800">
                          {item?.address?.name || "-"}
                        </div>
                      </td>

                      <td className="border-t border-[#f8dde1] px-5 py-4">
                        <span
                          className={[
                            "inline-flex rounded-full border px-3 py-1 text-[11px] font-semibold",
                            statusClass(item.status),
                          ].join(" ")}
                        >
                          {normalizeStatus(item.status)}
                        </span>
                      </td>

                      <td className="border-t border-[#f8dde1] px-5 py-4">
                        <div className="flex flex-col gap-1">
                          <span
                            className={[
                              "inline-flex w-fit rounded-full border px-3 py-1 text-[11px] font-semibold",
                              paymentClass(item.paymentStatus),
                            ].join(" ")}
                          >
                            {getPaymentDisplayLabel(item)}
                          </span>
                          <span className="text-[11px] font-semibold text-slate-400">
                            {getPaymentMethodLabel(item)}
                          </span>
                          {isPayOnDeliveryOrder(item) ? (
                            <span className="text-[11px] font-semibold text-slate-400">
                              OTP after payment
                            </span>
                          ) : null}
                        </div>
                      </td>

                 <td className="border-t border-[#f8dde1] px-5 py-4">
                        <div className="font-semibold text-slate-800">
                          {Number(item.itemCount || 0)} items
                        </div>
                      </td>

                     <td className="border-t border-[#f8dde1] px-5 py-4">
                        <div className="font-semibold text-slate-800">
                          {fmtMoney(pricing.grandTotal)}
                        </div>
                      </td>

                      <td className="border-t border-[#f8dde1] px-5 py-4 align-top">
  <DeliveryDriverCell order={item} delivery={item.delivery} />
</td>

                    <td className="border-t border-[#f8dde1] px-5 py-4">
                        <div className="text-slate-700">{fmtDate(item.createdAt)}</div>
                      </td>

                    <td className="border-t border-[#f8dde1] px-5 py-4 align-top" onClick={(e) => e.stopPropagation()}>
                        <RowStatusActions
                          order={item}
                          currentStatus={item.status}
                          loading={rowStatusLoadingId === item.id}
                          onChange={(nextStatus) => changeRowStatus(item.id, nextStatus)}
                        />
                      </td>

                    <td className="border-t border-[#f8dde1] px-4 py-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-center gap-1.5">
                          <button
                            type="button"
                            title="View order"
                            aria-label="View order"
                            onClick={(e) => {
                              e.stopPropagation();
                              openDetail(item.id);
                            }}
                            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] border border-[#f2c6cc] text-slate-600 transition hover:bg-[#fff8f9] hover:text-[#d86a78]"
                          >
                            <Eye size={15} />
                          </button>

                          {hasAssignedDriver(item) && !isCancelledRefundedOrder(item) ? (
                            <button
                              type="button"
                              title="Open map"
                              aria-label="Open map"
                              onClick={(e) => {
                                e.stopPropagation();
                                openTracking(item);
                              }}
                              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] border border-[#f2c6cc] text-slate-600 transition hover:bg-[#fff8f9] hover:text-[#d86a78]"
                            >
                              <MapPin size={15} />
                            </button>
                          ) : null}

                          {!isWarehouseOrderTerminal(item) ? (
                            <button
                              type="button"
                              title={item?.delivery?.partner ? "Change driver" : "Assign driver"}
                              aria-label={item?.delivery?.partner ? "Change driver" : "Assign driver"}
                              disabled={!canChangeDriverForOrder(item)}
                              onClick={(e) => {
                                e.stopPropagation();
                                openManualAssign(item);
                              }}
                              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] border border-[#f2c6cc] text-slate-600 transition hover:bg-[#fff8f9] hover:text-[#d86a78] disabled:cursor-not-allowed disabled:opacity-40"
                            >
                              <Truck size={15} />
                            </button>
                          ) : null}

                          {isUnclearPendingOrder(item) ? (
                            <button
                              type="button"
                              title="Close unclear pending order"
                              aria-label="Close unclear pending order"
                              onClick={(e) => {
                                e.stopPropagation();
                                openCancelOrder(item, "unclear");
                              }}
                              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] border border-amber-300 bg-amber-50 text-amber-700 transition hover:bg-amber-100"
                            >
                              <X size={15} />
                            </button>
                          ) : canCancelWarehouseOrder(item) ? (
                            <button
                              type="button"
                              title="Cancel order"
                              aria-label="Cancel order"
                              onClick={(e) => {
                                e.stopPropagation();
                                openCancelOrder(item, "refund");
                              }}
                              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] border border-rose-200 text-rose-600 transition hover:bg-rose-50"
                            >
                              <X size={15} />
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={10}
                    className="px-5 py-10 text-center text-[14px] text-slate-500"
                  >
                    No customer orders found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

      </div>

      <Pagination
        page={page}
        totalPages={totalPages}
        totalItems={meta.total}
        rowsPerPage={currentPageLimit}
        onPageChange={setPage}
        onRowsPerPageChange={(value) => {
          setRowsPerPage(value);
          setPage(1);
        }}
      />

      {detailOpen && detail ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/35 p-4">
          <div className="max-h-[90vh] w-full max-w-6xl overflow-hidden rounded-[24px] bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-[#f8dde1] px-6 py-4">
              <div>
                <h3 className="text-[18px] font-bold text-slate-800">
                  Customer Order Detail
                </h3>
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <WarehouseDailyNumberBadge order={detail} />
                  <div>
                    <p className="text-[13px] font-semibold text-slate-600">
                      {getReadableCustomerOrderId(detail)}
                    </p>
                    {getWarehouseDailyNumber(detail) ? (
                      <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
                        Write this packet number on the bag
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setDetailOpen(false)}
                className="rounded-[10px] p-2 text-slate-500 hover:bg-slate-100"
              >
                <X size={18} />
              </button>
            </div>

            <div className="max-h-[72vh] overflow-y-auto px-6 py-5">
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-[16px] border border-[#f2c6cc] p-4">
                  <div className="text-[12px] font-semibold text-slate-500">
                    Status
                  </div>
                  <div className="mt-3">
                    <span
                      className={[
                        "inline-flex rounded-full border px-3 py-1 text-[11px] font-semibold",
                        statusClass(detail.status),
                      ].join(" ")}
                    >
                      {normalizeStatus(detail.status)}
                    </span>
                  </div>
                </div>

                <div className="rounded-[16px] border border-[#f2c6cc] p-4">
                  <div className="flex items-center gap-2 text-[12px] font-semibold text-slate-500">
                    <CreditCard size={14} />
                    Payment
                  </div>
                  <div className="mt-2 text-[14px] font-semibold text-slate-800">
                    {getPaymentDisplayLabel(detail)}
                  </div>
                  <div className="mt-1 text-[12px] text-slate-400">
                    {getPaymentMethodLabel(detail)}
                  </div>
                  {isPayOnDeliveryOrder(detail) ? (
                    <div className="mt-2 rounded-[10px] bg-amber-50 px-3 py-2 text-[11px] font-semibold text-amber-700">
                      {String(detail.paymentStatus || '').toUpperCase() === 'PAID'
                        ? 'Razorpay payment received. Delivery OTP can be verified.'
                        : 'Waiting for Razorpay payment at delivery. OTP stays locked until payment is received.'}
                    </div>
                  ) : null}
                </div>

                <div className="rounded-[16px] border border-[#f2c6cc] p-4">
                  <div className="text-[12px] font-semibold text-slate-500">
                    Items
                  </div>
                  <div className="mt-2 text-[14px] font-semibold text-slate-800">
                    {Number(detail.itemCount || 0)} items
                  </div>
                </div>

                <div className="rounded-[16px] border border-[#f2c6cc] p-4">
                  <div className="text-[12px] font-semibold text-slate-500">
                    Grand Total
                  </div>
                  <div className="mt-2 text-[14px] font-semibold text-slate-800">
                    {fmtMoney(getPricing(detail).grandTotal)}
                  </div>
                </div>
              </div>

              <div className="mt-5 grid gap-4 lg:grid-cols-2">
                <div className="rounded-[16px] border border-[#f2c6cc] p-4">
                  <h4 className="text-[15px] font-bold text-slate-800">
                    Customer Info
                  </h4>
                  <div className="mt-3 space-y-2 text-[14px] text-slate-700">
                    <div>
                      <span className="font-semibold">Name:</span>{" "}
                      {detail?.address?.name || "-"}
                    </div>
                    <div>
                      <span className="font-semibold">Phone:</span>{" "}
                      {detail?.address?.phone || "-"}
                    </div>
                    <div>
                      <span className="font-semibold">City:</span>{" "}
                      {detail?.address?.city || "-"}
                    </div>
                    <div>
                      <span className="font-semibold">State:</span>{" "}
                      {detail?.address?.state || "-"}
                    </div>
                    <div>
                      <span className="font-semibold">Pincode:</span>{" "}
                      {detail?.address?.pincode || "-"}
                    </div>
                    <div>
                      <span className="font-semibold">Address:</span>{" "}
                      {[detail?.address?.addressLine1, detail?.address?.addressLine2]
                        .filter(Boolean)
                        .join(", ") || "-"}
                    </div>
                  </div>
                </div>

                <div className="rounded-[16px] border border-[#f2c6cc] p-4">
                  <h4 className="text-[15px] font-bold text-slate-800">
                    Pricing Info
                  </h4>
                  <div className="mt-3 space-y-2 text-[14px] text-slate-700">
                    <div>
                      <span className="font-semibold">Subtotal:</span>{" "}
                      {fmtMoney(getPricing(detail).subtotal)}
                    </div>
                    <div>
                      <span className="font-semibold">Delivery Fee:</span>{" "}
                      {fmtMoney(getPricing(detail).deliveryFee)}
                    </div>
                    <div>
                      <span className="font-semibold">Tax:</span>{" "}
                      {fmtMoney(getPricing(detail).taxAmount)}
                    </div>
                    <div>
                      <span className="font-semibold">Discount:</span>{" "}
                      {fmtMoney(getPricing(detail).discountAmount)}
                    </div>
                    <div>
                      <span className="font-semibold">Grand Total:</span>{" "}
                      {fmtMoney(getPricing(detail).grandTotal)}
                    </div>
                    <div>
                      <span className="font-semibold">Created:</span>{" "}
                      {fmtDate(detail.createdAt)}
                    </div>
                    <div>
                      <span className="font-semibold">Updated:</span>{" "}
                      {fmtDateTime(detail.updatedAt)}
                    </div>
                  </div>
                </div>
              </div>

              <CancellationRefundCard order={detail} />

              {!isCancelledRefundedOrder(detail) && detail?.pickupOtp ? (
                <div className="mt-5 rounded-[16px] border border-[#f2c6cc] bg-[#fff8f9] p-4">
                  <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div>
                      <h4 className="flex items-center gap-2 text-[15px] font-bold text-slate-800">
                        <KeyRound size={16} className="text-[#d86a78]" />
                        Warehouse Pickup OTP
                      </h4>
                      <p className="mt-1 text-[13px] text-slate-500">Driver must enter this 4 digit OTP before picking this order from warehouse.</p>
                    </div>
                    <div className="rounded-[14px] border border-[#f2c6cc] bg-white px-5 py-3 text-center">
                      <div className="font-mono text-[26px] font-black tracking-[0.28em] text-[#d86a78]">{detail.pickupOtp}</div>
                      <div className="mt-1 text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400">
                        {detail.pickupOtpVerifiedAt ? `Verified ${fmtDateTime(detail.pickupOtpVerifiedAt)}` : "Pending verification"}
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}

              <DeliveryDetailCard order={detail} delivery={detail.delivery} />

              <div className="mt-4 flex flex-wrap justify-end gap-2">
                {isUnclearPendingOrder(detail) ? (
                  <button
                    type="button"
                    onClick={() => openCancelOrder(detail, "unclear")}
                    className="inline-flex items-center gap-2 rounded-[12px] border border-amber-300 bg-amber-50 px-4 py-2 text-[13px] font-bold text-amber-700 hover:bg-amber-100"
                  >
                    <X size={16} />
                    Close Unclear Pending
                  </button>
                ) : canCancelWarehouseOrder(detail) ? (
                  <button
                    type="button"
                    onClick={() => openCancelOrder(detail, "refund")}
                    className="inline-flex items-center gap-2 rounded-[12px] border border-rose-200 bg-white px-4 py-2 text-[13px] font-bold text-rose-600 hover:bg-rose-50"
                  >
                    <X size={16} />
                    Cancel + Refund
                  </button>
                ) : null}
                {hasAssignedDriver(detail) && !isCancelledRefundedOrder(detail) ? (
                  <button
                    type="button"
                    onClick={() => openTracking(detail)}
                    className="inline-flex items-center gap-2 rounded-[12px] border border-[#f2c6cc] bg-white px-4 py-2 text-[13px] font-bold text-slate-600 hover:bg-[#fff8f9] hover:text-[#d86a78]"
                  >
                    <MapPin size={16} />
                    Open Map
                  </button>
                ) : null}
                {!isWarehouseOrderTerminal(detail) ? (
                  <button
                    type="button"
                    disabled={!canChangeDriverForOrder(detail)}
                    onClick={() => openManualAssign(detail)}
                    className="inline-flex items-center gap-2 rounded-[12px] bg-[#d86a78] px-4 py-2 text-[13px] font-bold text-white disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <Truck size={16} />
                    {detail?.delivery?.partner ? "Change Driver" : "Assign Driver"}
                  </button>
                ) : null}
              </div>

              <div className="mt-5 rounded-[16px] border border-[#f2c6cc] p-4">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <h4 className="text-[15px] font-bold text-slate-800">
                    Update Order Status
                  </h4>

                  <RowStatusActions
                    order={detail}
                    currentStatus={detail.status}
                    loading={statusUpdating}
                    onChange={changeStatus}
                  />
                </div>

                {message ? (
                  <div className="mt-3 rounded-[12px] bg-[#fff8f9] px-3 py-2 text-[13px] font-medium text-[#d86a78]">
                    {message}
                  </div>
                ) : null}
              </div>

              <div className="mt-5 overflow-hidden rounded-[16px] border border-[#f2c6cc]">
                <div className="bg-[#fff7f8] px-4 py-3 text-[14px] font-bold text-slate-700">
                  Items
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full min-w-[1320px]">
                    <thead>
                      <tr className="text-left text-[13px] font-semibold text-slate-600">
                        <th className="px-4 py-3">Product</th>
                        <th className="px-4 py-3">SKU</th>
                        <th className="px-4 py-3">Variant / Pack</th>
                        <th className="px-4 py-3">Ordered Qty</th>
                        <th className="px-4 py-3">Sold Batch</th>
                        <th className="px-4 py-3">Batch Sold Qty</th>
                        <th className="px-4 py-3">Batch Available Qty</th>
                        <th className="px-4 py-3">Batch Received Qty</th>
                        <th className="px-4 py-3">Expiry</th>
                        <th className="px-4 py-3">Unit Price</th>
                        <th className="px-4 py-3">Line Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(detail.items || []).map((item) => {
                        const batches = normalizeBatchAllocations(item);

                        return (
                          <tr
                            key={item.id}
                            className="border-t border-[#f8dde1] align-top text-[14px] text-slate-700"
                          >
                            <td className="px-4 py-3">
                              <div className="font-semibold text-slate-800">
                                {item.product?.name || "-"}
                              </div>
                              <div className="mt-1 text-[12px] text-slate-400">
                                UOM: {item.product?.unitSymbol || item.product?.unitName || "-"}
                              </div>
                            </td>
                            <td className="px-4 py-3">{item.product?.variantSku || item.product?.sku || "-"}</td>
                            <td className="px-4 py-3">
                              {[item.product?.variantName, item.product?.packLabel]
                                .filter(Boolean)
                                .join(" / ") || "-"}
                            </td>
                            <td className="px-4 py-3 font-semibold text-slate-800">
                              {fmtQty(item.qty)}
                            </td>
                            <td className="px-4 py-3">
                              <BatchStack
                                rows={batches}
                                empty="No batch audit"
                                render={(batch) => (
                                  <span className="inline-flex rounded-full bg-[#fff1f3] px-2.5 py-1 text-[12px] font-bold text-[#d86a78]">
                                    {batch.batchNo || batch.batchId || "-"}
                                  </span>
                                )}
                              />
                            </td>
                            <td className="px-4 py-3">
                              <BatchStack
                                rows={batches}
                                render={(batch) => (
                                  <span className="font-semibold text-slate-800">
                                    {fmtQty(batch.qty)}
                                  </span>
                                )}
                              />
                            </td>
                            <td className="px-4 py-3">
                              <BatchStack
                                rows={batches}
                                render={(batch) => (
                                  <span className="font-semibold text-slate-800">
                                    {batch.availableQty == null ? "-" : fmtQty(batch.availableQty)}
                                  </span>
                                )}
                              />
                            </td>
                            <td className="px-4 py-3">
                              <BatchStack
                                rows={batches}
                                render={(batch) => (
                                  <span className="font-semibold text-slate-800">
                                    {batch.receivedQty == null ? "-" : fmtQty(batch.receivedQty)}
                                  </span>
                                )}
                              />
                            </td>
                            <td className="px-4 py-3">
                              <BatchStack
                                rows={batches}
                                render={(batch) => (
                                  <div>
                                    <div className="font-semibold text-slate-800">
                                      {fmtDate(batch.expiryDate)}
                                    </div>
                                    <div className="mt-1 text-[11px] text-slate-400">
                                      MFG: {fmtDate(batch.mfgDate)}
                                    </div>
                                  </div>
                                )}
                              />
                            </td>
                            <td className="px-4 py-3">
                              {fmtMoney(item.unitPrice || 0)}
                            </td>
                            <td className="px-4 py-3 font-semibold text-slate-800">
                              {fmtMoney(item.lineTotal || 0)}
                            </td>
                          </tr>
                        );
                      })}

                      {!(detail.items || []).length ? (
                        <tr>
                          <td
                            colSpan={10}
                            className="px-4 py-6 text-center text-[14px] text-slate-500"
                          >
                            No items found
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <OrderTrackingMapModal
        open={trackingOpen}
        order={trackingOrder}
        tracking={trackingData}
        loading={trackingLoading}
        refreshing={trackingRefreshing}
        lastFetchedAt={trackingLastFetchedAt}
        message={trackingMessage}
        onClose={() => setTrackingOpen(false)}
        onRefresh={() => fetchTracking(trackingOrder?.id, { silent: true })}
      />


      {cancelTarget ? (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
          <div className="w-full max-w-lg rounded-[26px] bg-white p-6 shadow-2xl ring-1 ring-[#f2c6cc]">
            <div className="text-[11px] font-black uppercase tracking-wide text-[#d86a78]">{cancelMode === "unclear" ? "Close unclear pending order" : "Cancel + Razorpay refund"}</div>
            <h3 className="mt-2 text-2xl font-black text-slate-950">{getReadableCustomerOrderId(cancelTarget)}</h3>
            <p className="mt-2 text-sm font-semibold text-slate-500">
              {cancelMode === "unclear"
                ? `The backend will first verify Razorpay. It will close only an online payment that is still created/unknown after ${UNCLEAR_PENDING_CLOSE_MINUTES} minutes. Authorized or captured payments are blocked from manual close.`
                : "This will cancel the grocery order, cancel its delivery task, start the Razorpay refund, and run the existing stock rollback flow."}
            </p>
            <label className="mt-5 block text-xs font-black uppercase tracking-wide text-slate-500">{cancelMode === "unclear" ? "Close reason" : "Cancellation reason"}</label>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={4}
              className="mt-2 w-full rounded-[16px] border border-[#f2c6cc] p-3 text-sm font-semibold outline-none focus:ring-2 focus:ring-[#f2c6cc]"
              placeholder={cancelMode === "unclear" ? "Example: Payment is still unclear after the confirmation window" : "Example: Warehouse cannot fulfil this order / item unavailable"}
            />
            <div className="mt-5 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                disabled={cancelLoading}
                onClick={() => { setCancelTarget(null); setCancelMode("refund"); }}
                className="rounded-[12px] border border-[#f2c6cc] px-4 py-2 text-[13px] font-bold text-slate-600 disabled:opacity-60"
              >
                Close
              </button>
              <button
                type="button"
                disabled={cancelLoading || cancelReason.trim().length < 3}
                onClick={submitCancelOrder}
                className="rounded-[12px] bg-[#d86a78] px-4 py-2 text-[13px] font-bold text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {cancelLoading ? (cancelMode === "unclear" ? "Verifying..." : "Cancelling...") : (cancelMode === "unclear" ? "Verify + close pending" : "Cancel order + refund")}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <ManualAssignModal
        open={manualAssignOpen}
        order={manualAssignOrder}
        options={manualAssignOptions}
        loading={manualAssignLoading}
        assigningId={manualAssigningId}
        showUnavailable={manualAssignShowUnavailable}
        message={manualAssignMessage}
        onClose={() => setManualAssignOpen(false)}
        onToggleUnavailable={toggleUnavailablePartners}
        onAssign={assignDriver}
      />
    </div>
  );
}
